"use client";

import React from "react";
import { MODAL_IDS, useGlobalModalStack } from "@west-tennessee-consulting/ui";
import { Loader2, Search } from "lucide-react";
import { toast } from "sonner";
import { UnifiedModal } from "@/components/shared/modals";
import { Input } from "@/components/ui/input";
import { useModalContext } from "@/lib/hooks/ui/useModalContext";
import { useFTSSearch } from "@/lib/hooks/api/search/use-fts-search";
import type { ReassignCompanyModalData } from "@/lib/modals/data/modal-data-types";

// This modal is mounted globally (app/layout.tsx → DetailPageModals), OUTSIDE any
// DetailActionProvider. It CANNOT read onUpdateItem/onToast from DetailActionContext —
// that context is always null here. The chosen-contact updater (`onConfirm`) and the
// target `ticketId` arrive through the global-modal-stack data payload supplied by the
// reassignTicketCompany action handler. Enforced by validate-global-modal-context.mjs.

const MODAL_ID = MODAL_IDS.timeEntries.reassignCompany;

/**
 * Reassign a ticket's company/contact by picking the correct contact. The action
 * handler updates ticket.contact_id + company_id; DB triggers cascade the
 * denormalized company_name/contact_name onto the ticket and every time entry on it.
 */
export function ReassignCompanyModal(): React.ReactElement {
  const data = useModalContext<ReassignCompanyModalData>(MODAL_ID);
  const { closeModal } = useGlobalModalStack();
  const { search, results, isLoading } = useFTSSearch({ entityType: "contacts", limit: 25 });
  const [query, setQuery] = React.useState("");
  const [submittingId, setSubmittingId] = React.useState<string | null>(null);

  const onQueryChange = React.useCallback(
    (value: string) => {
      setQuery(value);
      search(value);
    },
    [search]
  );

  const onPick = React.useCallback(
    async (contactId: string) => {
      const onConfirm = data?.onConfirm;
      if (!onConfirm) {
        toast.error("Unable to reassign — no updater available");
        return;
      }
      try {
        setSubmittingId(contactId);
        await onConfirm(contactId);
        toast.success("Reassigned to the new company / contact");
        closeModal(MODAL_ID);
      } catch (error) {
        console.error("Failed to reassign company/contact:", error);
        toast.error("Failed to reassign");
      } finally {
        setSubmittingId(null);
      }
    },
    [data, closeModal]
  );

  const trimmed = query.trim();

  return (
    <UnifiedModal
      modalId={MODAL_IDS.timeEntries.reassignCompany}
      title="Reassign company / contact"
      description="Pick the correct contact. The ticket — and every time entry on it — moves to that contact's company."
      size="md"
      forceModal
    >
      {data?.ticketId ? (
        <div className="space-y-4">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              type="text"
              autoFocus
              value={query}
              onChange={(e) => onQueryChange(e.target.value)}
              placeholder="Search contacts by name, email, or company…"
              className="pl-9"
            />
          </div>

          <div className="max-h-72 overflow-y-auto rounded-md border">
            {isLoading && (
              <div className="flex items-center justify-center gap-2 py-6 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" /> Searching…
              </div>
            )}

            {!isLoading && trimmed.length === 0 && (
              <div className="py-6 text-center text-sm text-muted-foreground">
                Start typing to search contacts.
              </div>
            )}

            {!isLoading && trimmed.length > 0 && results.length === 0 && (
              <div className="py-6 text-center text-sm text-muted-foreground">
                No contacts match “{trimmed}”.
              </div>
            )}

            {results.map((contact) => {
              const name = typeof contact.name === "string" ? contact.name : "Unnamed contact";
              const companyName =
                typeof contact.company_name === "string" ? contact.company_name : null;
              const email = typeof contact.email === "string" ? contact.email : null;
              return (
                <button
                  type="button"
                  key={contact.id}
                  onClick={() => onPick(contact.id)}
                  disabled={submittingId !== null}
                  className="flex w-full items-center justify-between gap-3 border-b px-3 py-2 text-left text-sm last:border-b-0 hover:bg-accent disabled:opacity-50"
                >
                  <span className="min-w-0">
                    <span className="block truncate font-medium">{name}</span>
                    <span className="block truncate text-xs text-muted-foreground">
                      {companyName ?? "No company"}
                      {email ? ` · ${email}` : ""}
                    </span>
                  </span>
                  {submittingId === contact.id && (
                    <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">Unable to load ticket context.</p>
      )}
    </UnifiedModal>
  );
}
